import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiResponse, QueryOptions } from '@cast/shared-types';
import { makeIsEnabled } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';

import { useOrganizations } from '@cast/shared-organization-context-data-access';

const apiFn = apiClient.clusterManagement.getComponentStatus;

const CLUSTER_MANAGEMENT_COMPONENT_STATUS_QUERY_KEY =
  'cluster-management-component-status';

const getComponentStatusQueryKey = (
  clusterId: string,
  componentId: string | undefined
) =>
  [
    CLUSTER_MANAGEMENT_COMPONENT_STATUS_QUERY_KEY,
    clusterId,
    componentId,
  ] as const;

type ComponentStatusQueryData = ApiResponse<typeof apiFn>;

type ComponentStatusQueryArgs<SELECT> = {
  clusterId: string;
  componentId?: string;
} & QueryOptions<ComponentStatusQueryData, SELECT>;

const getComponentStatusQueryOptions = <SELECT = ComponentStatusQueryData>({
  clusterId,
  componentId,
  organizationId,
  ...rest
}: ComponentStatusQueryArgs<SELECT> & { organizationId: string | undefined }) =>
  queryOptions({
    queryKey: getComponentStatusQueryKey(clusterId, componentId),
    queryFn: async () => {
      const { data } = await apiFn({
        organizationId: organizationId!,
        clusterId,
        componentId: componentId!,
      });

      return data;
    },
    ...rest,
    enabled: makeIsEnabled(!!componentId, !!organizationId, rest.enabled),
  });

export const useComponentStatusQuery = <SELECT = ComponentStatusQueryData>({
  clusterId,
  componentId,
  ...rest
}: ComponentStatusQueryArgs<SELECT>) => {
  const { currentOrganization } = useOrganizations();
  const queryClient = useQueryClient();
  const options = getComponentStatusQueryOptions({
    clusterId,
    componentId,
    organizationId: currentOrganization?.id,
    ...rest,
  });

  return {
    ...useQuery(options),
    removeQuery: () =>
      queryClient.removeQueries({ queryKey: options.queryKey }),
  };
};
